import { ChangeDetectorRef, Component, DestroyRef, HostListener, inject, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { Observable } from 'rxjs';
import { PageHeader } from '../../../shared/page-header/page-header';
import { Banner } from '../../../shared/banner/banner';
import { AppButtons } from '../../../shared/app-buttons/app-buttons';
import { LeadInfo, LeadInfoData, LeadInfoDialogData } from './lead-info/lead-info';
import { RequoteRemarkCard } from "../../../shared/requote-remark-card/requote-remark-card";
import { ApplicantReviewSummaryCard } from './applicant-review-summary-card/applicant-review-summary-card';
import { Tabs } from '../../../shared/tabs/tabs';
import { UploadDocumentsPreview } from './upload-documents-preview/upload-documents-preview';
import { MafAppReview } from './maf-app-review/maf-app-review';
import { ApplicantProfile } from "./applicant-profile/applicant-profile";
import {
  ApplicantAdditionalInfo,
  ApplicantAdditionalInfoApi,
  ApplicantBasicInfoApi,
  ApplicantField,
  ApplicantProfileData,
  SingleApplicantBasicInfo,
  SingleApplicantData
} from '../../../shared/models/applicant-profile.model';
import { FormPopup, FormPopupConfig } from '../../../shared/form-popup/form-popup';
import {
  ApplicationReviewLeadContext,
  MarkOrUnmarkPayload,
  ReviewActionType,
  ReviewAssignmentAction,
  ReviewAssignmentStatus,
  ReviewerAssignmentLead,
  ReviewerAssignmentPayload,
  SubmitApplicationReadyPayload,
  SupportAction,
  SupportActionType
} from '../constants/application-review.model';
import { AppDropdownComponent, DropdownOption } from '../../../shared/app-dropdown/app-dropdown.component';
import { PopupModal } from '../../../shared/popup-modal/popup-modal';
import { PopupButtonsConfig } from '../../../shared/models/popup-buttons.model';
import { FileUpload } from '../../../shared/file-upload/file-upload';
import { UnsavedChangesPopup } from '../../../shared/unsaved-changes-popup/unsaved-changes-popup';
import { ReviewQueueService } from '../../services/review-queue.service';
import { ToasterService } from '../../../shared/toaster/toaster.service';
import { AppTooltipComponent } from '../../../shared/app-tooltip/app-tooltip.component';
import { Images } from '../../../core/constants/image.constants';
import { ApplicationReviewService } from '../../services/application-review.service';
import { DateTimeConversionHelper } from '../../../shared/helper/date-time-conversion.helper';
import { ReviewQueueSharedService } from '../../services/review-queue-shared.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ComponentCanDeactivate } from '../../../core/guards/unsaved-changes.guard';
import { ApplicantDropdownOption, RELATIONSHIP_TO_SELF_OPTIONS, SPONSOR_EMIRATE_OPTIONS, SPONSOR_SALARY_BAND_OPTIONS, SPONSOR_TYPE_OPTIONS, WHO_IS_SPONSOR_OPTIONS } from '../../../shared/models/applicant-profile.constants';
import { ReviewStatus, ReviewTab } from '../review-queue/constants/review-queue.model';
import { UnsavedChangesPopupSharedService } from '../../../shared/services/unsaved-changes-popup-shared.service';
import { agentObjectSignal, currentUserProfileSignal } from '../../../core/constants/auth-state';
import { AppAction } from '../../../core/permission-config/permission-config';
import { UserPermissionService } from '../../../core/services/user-permission.service';
import { AppReviewApiService } from '../services/app-review-api.service';
import { UserAgentDropDownPayload } from '../../add-lead/add-lead';
import { LoaderService } from '../../../shared/loader/loader.service';
import { AgentContactCard, ContactCardDetails } from '../../../shared/agent-contact-card/agent-contact-card';

type VerifyDetailsTabLabel = 'Applicant Profile' | 'MAF' | 'Documents';

interface VerifyDetailsTab {
  iconName: string;
  label: VerifyDetailsTabLabel;
  iconNameSuffix: string;
}

type ApplicantVerifyFlags = {
  isApplicantVerified: boolean;
  isMafVerified: boolean;
  isDocumentsVerified: boolean;
};
import { LeadService } from '../../services/lead-service';
import { UserService } from '../../../core/services/user.service';

@Component({
  selector: 'app-application-review',
  imports: [
    AgentContactCard,
    CommonModule,
    ReactiveFormsModule,
    MatIconModule,
    RouterOutlet,
    PageHeader,
    Banner,
    AppButtons,
    LeadInfo,
    RequoteRemarkCard,
    Tabs,
    ApplicantReviewSummaryCard,
    UploadDocumentsPreview,
    MafAppReview,
    ApplicantProfile,
    FormPopup,
    AppDropdownComponent,
    PopupModal,
    FileUpload,
    AppTooltipComponent,
    UnsavedChangesPopup,
  ],
  templateUrl: './application-review.html',
  styleUrl: './application-review.scss',
})
export class ApplicationReview implements OnInit, OnDestroy, ComponentCanDeactivate {
  contactCardDetails: ContactCardDetails | null = null;
  contactCardType: 'Agent' | 'Client' = 'Agent';
  @ViewChild(ApplicantProfile) private readonly applicantProfileRef?: ApplicantProfile;
  @ViewChild('uploadDocumentsPreviewRef') private readonly uploadDocumentsPreviewRef?: UploadDocumentsPreview;

  private static readonly BASIC_INFO_FIELD_KEYS = [
    'firstName',
    'lastName',
    'dob',
    'gender',
    'emirateOfVisaInsurance',
    'maritalStatus',
    'emirateOfVisaInsuranceId',
  ] as const;

  images = Images;
  readonly AppAction = AppAction;
  documentsSelected = false; // Set this to true when documents are selected
  applicantSelectedIndex = 0;
  verifySelectedIndex = 0;
  applicantReviewedStates: boolean[] = [];
  checklistStatus = {
    mafUploaded: true,
    mandatoryDocsUploaded: true,
    extractedFieldsFinalized: false,
  };
  verifyDetailsTabStatus = [];
  isApplicantVerified = false;
  isMafVerified = false;
  isDocumentsVerified = false;

  applicantProfileData: ApplicantProfileData | null = null;
  private readonly ApplicationReviewService = inject(ApplicationReviewService);
  private readonly appReviewApiService = inject(AppReviewApiService, { optional: true });
  readonly unsavedChangesService = inject(UnsavedChangesPopupSharedService);
  private reviewQueueSharedService = inject(ReviewQueueSharedService);
  readonly userPermissionService = inject(UserPermissionService);
  private readonly loaderService = inject(LoaderService);

  applicantTabs: Array<{ applicantId?: string, iconName: string; label: string; description: string; iconNameSuffix?: string }> = [];
  private applicantVerifyFlags: Record<string, ApplicantVerifyFlags> = {};
  private emiratesMap: Record<string, string> = {};

  private readonly defaultVerifyDetailsTabs: VerifyDetailsTab[] = [
    { iconName: 'icon-user-outline', label: 'Applicant Profile', iconNameSuffix: 'del-warning' },
    { iconName: 'application_maf_icon', label: 'MAF', iconNameSuffix: 'del-warning' },
    { iconName: 'file-upload', label: 'Documents', iconNameSuffix: 'del-warning' },
  ];

  verifyDetailsTabs: VerifyDetailsTab[] = [...this.defaultVerifyDetailsTabs];

  headerTitle = 'Application Review';
  statusLabel: ReviewAssignmentStatus = 'Review In-Progress';
  customVarient: 'success' | 'warning' | 'neutral' | 'error' | 'custom' =
    'custom';
  statusBgColor = '#FFEAAE';
  statusTextColor = '#7C5111';
  reviewMode = true;
  isViewMode = false;
  isUnassigned = false;
  showAssignPopup = false;
  showReassignPopup = false;
  showRemarkPopup = false;
  showRequoteRemarkPopup = false;
  remarksForm: FormGroup;
  isSavingRemarks = false;
  private postSaveRefreshTimer: ReturnType<typeof setTimeout> | null = null;
  private postSaveSuccessMessage: string | null = null;
  assignmentLeadContext: ApplicationReviewLeadContext | null = null;

  readonly remarkPopupConfig: FormPopupConfig = {
    header: {
      iconName: 'message-icon',
      title: 'Add Remarks',
      subtitle: '',
      description:
        'Add general observations or notes about this lead application',
    },
    fields: [
      {
        name: 'remarks',
        type: 'textarea',
        label: 'Remarks',
        placeholder: 'Enter your remarks, observations, or notes here...',
        // required: true,
        validators: {
          required: true,
          maxLength: 5000,
        },
      },
    ],
    action: {
      submitLabel: 'Save Remarks',
    },
  };

  readonly requoteRemarkPopupConfig: FormPopupConfig = {
    header: {
      iconName: 'message-icon',
      title: 'Agent Remarks',
      subtitle: '',
      description: '',
    },
    fields: [],
    action: {
      submitLabel: '',
    },
  };

  popupConfig: FormPopupConfig = {
    header: {
      iconName: 'icon-person',
      subtitle: 'Assign',
      title: 'Reviewer',
    },
    selectedInfo: {},
    fields: [],
    action: {
      submitLabel: 'Assign',
    },
  };

  assignmentReviewerOptions: DropdownOption[] = [
    { value: 'RV-1001', label: 'Sarah Jeremiah' },
    { value: 'RV-1002', label: 'Alisha Esther' },
    { value: 'RV-1003', label: 'Ramon Jose' },
  ];

  showSupportPopup = false;
  selectedSupportAction: SupportActionType | null = null;
  private isDocumentRequoteFlowActive = false;
  showMarkReadyConfirmPopup = false;
  showReopenApplicantConfirmPopup = false;
  inquiryForm: FormGroup;
  requoteForm: FormGroup;
  inquiryAttachedFiles: File[] = [];
  showRequoteConfirmPopup = false;
  lastSaved = '';
  private readonly dateTimeHelper = DateTimeConversionHelper;
  selectedLeadReviewStatus: ReviewTab | null = 'Unassigned';
  isSponsorSelf: boolean = false;
  requoteRemarkRefreshKey = 0;
  inquiryApplicantOptions: DropdownOption[] = [];
  inquiryDocumentTypeOptions: DropdownOption[] = [];
  assignedAgentOptions: DropdownOption[] = [];

  // Requote status banner
  showRequoteBanner = false;
  requoteBannerTitle = '';
  requoteBannerDescription = '';
  isRequoteDeclined = false;

  readonly supportPopupConfig: FormPopupConfig = {
    header: {
      iconName: 'icon-agent-support',
      title: 'Agent Support',
      subtitle: '',
      description: 'Select an action to communicate with the assigned agent',
    },
    fields: [],
    action: {
      submitLabel: 'Submit',
    },
  };

  readonly supportActions: SupportAction[] = [
    {
      action: 'inquireDataDoc',
      iconName: 'icon-help-circle',
      title: 'Inquire on Data/Doc',
      description:
        'Ask the agent to clarify or provide missing data/documents.',
    },
    {
      action: 'requestRequote',
      iconName: 'requote_icon',
      title: 'Request Requote',
      description:
        'Request a new quote from the agent due to data discrepancies.',
    },
  ];

  readonly supportQuickActions = [
    {
      action: 'contactAgent',
      iconName: 'phone_in_talk',
      title: 'Contact Agent',
    },
    {
      action: 'contactClient',
      iconName: 'person_outline',
      title: 'Contact Client',
    },
  ] as const;

  readonly requoteConfirmMessage = `<p class="support-requote-confirm-message">This action will update the application status from <strong>Application Review</strong> to <strong>Requote Requested</strong> and notify the assigned agent.</p><p class="support-requote-confirm-note">The agent will need to submit a new quote before the application can proceed.</p>`;

  readonly requoteConfirmButtons: PopupButtonsConfig[] = [
    {
      buttons: [
        {
          label: 'Confirm Requote',
          variant: 'primary',
          action: () => this.onConfirmRequote(),
        },
        {
          label: 'Cancel',
          variant: 'secondary',
          action: () => this.closeRequoteConfirmPopup(),
        },
      ],
    },
  ];
  readonly markReadyConfirmButtons: PopupButtonsConfig[] = [
    {
      buttons: [
        {
          label: 'Confirm',
          variant: 'primary',
          action: () => this.onConfirmMarkAsReady(),
        },
        {
          label: 'Cancel',
          variant: 'secondary',
          action: () => this.closeMarkAsReadyPopup(),
        },
      ],
    },
  ];

  readonly reopenApplicantConfirmButtons: PopupButtonsConfig[] = [
    {
      buttons: [
        {
          label: 'Reopen for Review',
          variant: 'primary',
          action: () => this.onConfirmReopenApplicantReview(),
        },
        {
          label: 'Cancel',
          variant: 'secondary',
          action: () => this.closeReopenApplicantPopup(),
        },
      ],
    },
  ];

  leadInfoData: LeadInfoData = {
    clientName: '',
    clientInitials: '',
    leadId: '',
    lastActivityText: '',
    lastActivityTime: '',
    quoteReference: '',
    quoteVersion: '',
    insurer: '',
    planName: '',
    network: '',
    leadCreatedOn: '',
    noOfApplicants: 0,
    assignedAgent: '',
    assignedReviewer: '',
  };

  selectedLeadData: any;
  private routeMode: 'view' | 'review' | 'requote' | null = null;

  constructor(
    protected readonly route: ActivatedRoute,
    private readonly fb: FormBuilder,
  ) {
    this.inquiryForm = this.fb.group({
      query: ['', [Validators.required, Validators.maxLength(1000)]],
      applicantInfoId: [null, Validators.required],
      documentTypeIds: [[]],
    });

    this.requoteForm = this.fb.group({
      reason: ['', [Validators.required, Validators.maxLength(1000)]],
    });

    this.remarksForm = this.fb.group({
      remarks: ['', [Validators.required, Validators.maxLength(5000)]],
    });
  }

  private readonly reviewQueueService = inject(ReviewQueueService);
  private readonly toaster = inject(ToasterService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly leadService = inject(LeadService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly ngZone = inject(NgZone);
  private readonly router = inject(Router);

  appReviewData: any;

  ngOnInit(): void {
    this.applicantReviewedStates = this.applicantTabs.map(() => false);

    const selectedLead = this.reviewQueueSharedService.selectedLeadData();

    if (!selectedLead) {
      // Nothing to review — someone hit this route directly or lost state (e.g. hard refresh).
      // Since leadId/mode are intentionally not in the URL, there is no safe way to recover;
      // send them back to pick a lead again.
      void this.router.navigate(['/review-queue']);
      return;
    }

    this.routeMode = this.reviewQueueSharedService.leadMode();
    this.selectedLeadData = selectedLead;

    const routeLeadId = selectedLead.lead_Id ?? selectedLead.id ?? '';
    if (routeLeadId) {
      this.leadInfoData = { ...this.leadInfoData, leadId: routeLeadId };
    }

    this.selectedLeadReviewStatus = this.resolveReviewStatus(
      selectedLead.review_Status ?? selectedLead.status,
    );

    const selectedStatus = this.mapReviewTabToStatusLabel(this.selectedLeadReviewStatus);
    const clientName = `${selectedLead.firstName ?? ''} ${selectedLead.lastName ?? ''}`.trim();

    this.leadInfoData = {
      clientName: selectedLead.contactName ?? clientName ?? '',
      clientInitials: this.getInitials(selectedLead.firstName, selectedLead.lastName),
      leadId: selectedLead.lead_Id ?? routeLeadId ?? '',
      lastActivityText: selectedLead.lastActivity ?? '',
      lastActivityTime: '',
      quoteReference: selectedLead.quoteId ?? '',
      quoteVersion: selectedLead.quoteVersion ?? '',
      insurer: selectedLead.insurer ?? '',
      planName: selectedLead.plan_Name ?? '',
      network: selectedLead.network_Name ?? '',
      leadCreatedOn: DateTimeConversionHelper.toDayMonthYear(selectedLead.dateCreated),
      noOfApplicants: selectedLead.applicants ?? 0,
      assignedAgent: selectedLead.assignedAgent ?? '',
      assignedReviewer: selectedLead.reviewerAssigned ?? '',
    };

    this.applyReviewStatus(selectedStatus);

    this.assignmentLeadContext = {
      lastActivity: selectedLead?.lastActivity ?? this.leadInfoData.lastActivityText,
      network: selectedLead?.network_Name ?? this.leadInfoData.network,
      applicants: selectedLead?.applicants ?? this.leadInfoData.noOfApplicants,
      priority: selectedLead?.priority ?? null,
      id: selectedLead?.id,
      leadId: selectedLead?.lead_Id ?? routeLeadId ?? this.leadInfoData.leadId,
      firstName: selectedLead?.firstName,
      lastName: selectedLead?.lastName,
      status: this.statusLabel,
      reviewerAssigned: this.leadInfoData.assignedReviewer,
    };

    this.loadEmiratesDropdown();
    this.loadReviewerOptions();
    this.loadAssignedAgentOptions();
    this.FetchApplicationReviewData();

    // Only refreshApplicationReview is a legitimate query param now — everything
    // identifying the lead (leadId, mode, status) has been removed from the URL.
    this.route.queryParams
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        if (params['refreshApplicationReview']) {
          this.FetchApplicationReviewData();
        }
      });
  }

  private getInitials(firstName?: string, lastName?: string): string {
    return (
      `${firstName?.charAt(0) ?? ''}${lastName?.charAt(0) ?? ''}`.toUpperCase() ||
      'NA'
    );
  }

  onAssign(): void {
    if (this.isViewMode && !this.canAssignInViewMode) {
      return;
    }

    this.openReviewerPopup('assign');
  }

  onReassign(): void {
    if (this.isViewMode) {
      return;
    }

    this.openReviewerPopup('reassign');
  }

  closeReviewerPopup(): void {
    this.showAssignPopup = false;
    this.showReassignPopup = false;
  }

  handleReviewerPopupSubmit(formValue: Record<string, unknown>): void {
    const reviewerId =
      typeof formValue['reviewerId'] === 'string'
        ? formValue['reviewerId'].trim()
        : '';
    const reassignedReason =
      typeof formValue['reassignedReason'] === 'string'
        ? formValue['reassignedReason'].trim()
        : '';
    if (!reviewerId) {
      return;
    }

    const action: ReviewAssignmentAction = this.showAssignPopup
      ? 'assign'
      : 'reassign';
    const payload = this.buildReviewerAssignmentPayload(reviewerId, reassignedReason);

    this.reviewQueueService.subscribeWithCleanup(
      this.reviewQueueService.AssignLead(payload),
      (response: any) => {
        const assignedReviewer = this.getReviewerLabel(reviewerId);
        this.leadInfoData = {
          ...this.leadInfoData,
          assignedReviewer,
        };

        if (action === 'assign') {
          // Move out of unassigned state immediately so the header badge reflects
          // the expected post-assignment review state without waiting for reload.
          this.selectedLeadReviewStatus = 'InProgress';
          this.applyReviewStatus('Review In-Progress');
          this.selectedLeadData = {
            ...this.selectedLeadData,
            review_Status: ReviewStatus.InProgress,
            status: ReviewStatus.InProgress,
            reviewerAssigned: assignedReviewer,
          };
        }

        if (this.assignmentLeadContext) {
          this.assignmentLeadContext = {
            ...this.assignmentLeadContext,
            reviewerAssigned: assignedReviewer,
            status: this.statusLabel,
          };
        }

        this.toaster.show({
          message: response.messages,
          status: 'success',
          position: 'top-right',
        });

        this.closeReviewerPopup();
        this.onDocumentReviewStatusRefreshRequested();
      },
      this.destroyRef,
    );
  }

  onSave(): void {
    if (this.isViewMode) {
      return;
    }

    if (!this.userPermissionService.canShow(AppAction.ManageDocument)) {
      return;
    }

    if (this.isOverallSaveDisabled) {
      return;
    }

    if (!this.uploadDocumentsPreviewRef) {
      this.toaster.show({
        message: 'Document save is not available right now. Please refresh and try again.',
        status: 'error',
        position: 'top-right',
      });
      return;
    }

    if (!this.hasUnsavedDocumentChanges()) {
      this.toaster.show({
        message: 'No changes to save.',
        status: 'error',
        position: 'top-right',
      });
      return;
    }

    this.uploadDocumentsPreviewRef.saveAllDocumentChanges();
  }

  canDeactivate(): boolean | Observable<boolean> {
    return this.unsavedChangesService.canDeactivate(() => this.hasUnsavedReviewChanges());
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (!this.hasUnsavedReviewChanges()) {
      return;
    }

    event.preventDefault();
  }

  onSupport(): void {
    if (this.isViewMode) {
      return;
    }

    if (!this.userPermissionService.canShow(AppAction.ManageApplicationReview)) {
      return;
    }

    this.showSupportPopup = true;
  }

  openRequestRequoteFromDocumentWarning(prefilledReason: string): void {
    if (this.isViewMode) {
      return;
    }

    this.isDocumentRequoteFlowActive = true;
    this.showSupportPopup = true;
    this.selectedSupportAction = 'requestRequote';
    this.requoteForm.patchValue({
      reason: prefilledReason || '',
    });
    this.requoteForm.markAsPristine();
  }

  requestRequoteDirectFromDocumentWarning(prefilledReason: string): void {
    if (this.isViewMode) {
      return;
    }

    const reason = String(prefilledReason ?? '').trim();
    if (!reason) {
      return;
    }

    this.submitRequoteRequest(reason);
  }

  navigateBackToReviewQueue(): void {
    void this.router.navigate([this.getBackRoute()]);
  }

  private getBackRoute(): string {
    switch (this.reviewQueueSharedService.entrySource()) {
      case 'pipeline':
        return '/pipeline';
      case 'leadManagement':
        return '/lead-management';
      default:
        return '/review-queue';
    }
  }

  closeSupportPopup(): void {
    this.showSupportPopup = false;
    this.selectedSupportAction = null;
    this.isDocumentRequoteFlowActive = false;
    this.showRequoteConfirmPopup = false;
    this.resetInquiryForm();
    this.resetRequoteForm();
  }

  onSupportActionSelect(action: SupportActionType): void {
    this.selectedSupportAction = action;
    if (action === 'inquireDataDoc') {
      this.prefillInquiryApplicant();
      this.loadInquiryDocumentTypes();
    }
  }

  onSupportQuickActionSelect(action: 'contactAgent' | 'contactClient'): void {
    const leadId = this.selectedLeadData?.lead_Id ?? this.selectedLeadData?.id ?? '';
    if (!leadId) {
      this.toaster.show({
        message: 'Lead id is missing. Please refresh and try again.',
        status: 'error',
        position: 'top-right',
      });
      return;
    }

    const leadInfoId = this.selectedLeadData?.leadInfoId ?? this.appReviewData?.leadInfoId ?? '';
    const userType = action === 'contactAgent' ? 'Agent' : 'Client';
    this.contactCardDetails = null;
    const fullLeadInfo = this.appReviewData?.leadInfo ?? (this.ApplicationReviewService as any).getCurrentLeadInfo?.();
    const leadInfo = fullLeadInfo ?? {
      id: leadInfoId || leadId,
      leadId,
      firstName: this.selectedLeadData?.firstName,
      lastName: this.selectedLeadData?.lastName,
      leadStatus: this.selectedLeadData?.leadStatus,
      leadStage: this.selectedLeadData?.leadStage,
    };
    this.ApplicationReviewService.getContactCard(leadId, userType, leadInfo)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          const details = userType === 'Agent' ? response.body?.agent : response.body?.customer;
          if (!details) {
            this.toaster.show({ message: `${userType} contact details are unavailable.`, status: 'warning' });
            return;
          }
          this.contactCardType = userType;
          this.contactCardDetails = { ...details };
          this.showSupportPopup = false;
        },
        error: () => { },
      });
  }

  onBackToSupportActions(): void {
    if (this.selectedSupportAction === 'requestRequote' && this.isDocumentRequoteFlowActive) {
      this.showSupportPopup = false;
      this.selectedSupportAction = null;
      this.showRequoteConfirmPopup = false;
      this.resetRequoteForm();
      this.uploadDocumentsPreviewRef?.reopenRequoteWarningModal();
      this.isDocumentRequoteFlowActive = false;
      return;
    }

    this.selectedSupportAction = null;
    this.isDocumentRequoteFlowActive = false;
    this.resetInquiryForm();
    this.resetRequoteForm();
  }

  onInquiryFilesSelected(files: File[]): void {
    this.inquiryAttachedFiles = files;
  }

  get isInquirySendDisabled(): boolean {
    return this.inquiryForm.invalid;
  }

  onSendInquiry(): void {
    if (this.inquiryForm.invalid) {
      this.inquiryForm.markAllAsTouched();
      return;
    }

    const selectedDocumentTypeIds =
      this.inquiryForm.get('documentTypeIds')?.value;
    const formData = new FormData();
    formData.append(
      'reviewQueueId',
      this.selectedLeadData?.reviewQueueId || '',
    );
    formData.append(
      'leadInfoId',
      this.selectedLeadData?.id || this.selectedLeadData?.leadInfoId || '',
    );
    const mafFinalSubmissionId =
      this.appReviewData?.mafInfo?.mafFinalSubmissionId ??
      this.selectedLeadData?.mafFinalSubmissionId ??
      '';
    if (mafFinalSubmissionId) {
      formData.append('mafFinalSubmissionId', mafFinalSubmissionId);
    }
    formData.append('reviewStatus', 'AgentFollowUp');
    formData.append('followUp.type', 'DataDocInquiry');
    formData.append(
      'followUp.agentQuery',
      (this.inquiryForm.get('query')?.value || '').trim(),
    );
    formData.append(
      'followUp.applicantInfoId',
      this.inquiryForm.get('applicantInfoId')?.value || '',
    );

    this.inquiryAttachedFiles.forEach((file, index) => {
      formData.append(`followUp.documents[${index}].file`, file, file.name);
      formData.append(`followUp.documents[${index}].documentInfoId`, '');
    });

    (Array.isArray(selectedDocumentTypeIds)
      ? selectedDocumentTypeIds
      : []
    ).forEach((id: string, index: number) => {
      formData.append(`followUp.documentTypeIds[${index}].documentTypeId`, id);
    });

    this.reviewQueueService
      .updateReviewQueueStatusFormData(formData)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          this.toaster.show({
            message: response?.messages?.[0] || 'Inquiry sent successfully',
            status: 'success',
            position: 'top-right',
          });
          this.closeSupportPopup();
        },
        error: () => { },
      });
  }

  get isRequoteProceedDisabled(): boolean {
    return this.requoteForm.invalid;
  }

  onProceedRequote(): void {
    if (!this.userPermissionService.canShow(AppAction.ManageApplicationReview)) {
      return;
    }

    if (this.requoteForm.invalid) {
      this.requoteForm.markAllAsTouched();
      return;
    }

    this.showRequoteConfirmPopup = true;
  }

  closeRequoteConfirmPopup(): void {
    this.showRequoteConfirmPopup = false;
  }

  onConfirmRequote(): void {
    const reason = (this.requoteForm.get('reason')?.value || '').trim();
    if (!reason) {
      this.requoteForm.markAllAsTouched();
      return;
    }

    this.submitRequoteRequest(reason, () => this.closeSupportPopup());
  }

  private submitRequoteRequest(reason: string, onSuccess?: () => void): void {

    const payload = {
      leadInfoId:
        this.selectedLeadData?.id || this.selectedLeadData?.leadInfoId || '',
      reviewQueueId: this.selectedLeadData?.reviewQueueId || '',
      reason,
      AgentId: this.appReviewData?.assignedAgentId ?? this.selectedLeadData?.assignedAgentId ?? '',
    };

    this.ApplicationReviewService.createReviewRequote(payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          this.toaster.show({
            message:
              response?.messages?.[0] || 'Requote requested successfully',
            status: 'success',
            position: 'top-right',
          });

          // Keep requote UX consistent regardless of request source (support popup or document warning).
          this.routeMode = 'requote';
          this.applyReviewStatus('Requote Requested');

          this.requoteRemarkRefreshKey++;
          onSuccess?.();
        },
        error: () => { },
      });
  }

  private resetInquiryForm(): void {
    const selectedApplicant = this.getSelectedApplicantId();
    this.inquiryForm.reset({
      query: '',
      applicantInfoId: selectedApplicant,
      documentTypeIds: [],
    });
    this.inquiryAttachedFiles = [];
  }

  private prefillInquiryApplicant(): void {
    this.inquiryForm.patchValue({
      applicantInfoId: this.getSelectedApplicantId(),
    });
  }

  private getSelectedApplicantId(): string | null {
    return this.applicantTabs[this.applicantSelectedIndex]?.applicantId ?? null;
  }

  private loadInquiryDocumentTypes(): void {
    if (!this.userPermissionService.canShow(AppAction.GetAllDocTypes)) {
      this.inquiryDocumentTypeOptions = [];
      return;
    }

    if (this.inquiryDocumentTypeOptions.length > 0) {
      return;
    }
    this.reviewQueueService.subscribeWithCleanup(
      this.reviewQueueService.getAllDocumentTypes(),
      (response: any) => {
        const body = Array.isArray(response?.body) ? response.body : [];
        this.inquiryDocumentTypeOptions = body
          .map(
            (item: any): DropdownOption => ({
              value: item?.id ? String(item.id) : '',
              label: item?.documentTypeName
                ? String(item.documentTypeName)
                : '',
            }),
          )
          .filter((option: DropdownOption) => !!option.value && !!option.label);
      },
      this.destroyRef,
    );
  }

  private resetRequoteForm(): void {
    this.requoteForm.reset({
      reason: '',
    });
  }

  private fetchRequoteBannerInfo(): void {
    const leadInfoId = this.selectedLeadData?.id || this.selectedLeadData?.leadInfoId || '';
    if (!leadInfoId) return;

    this.ApplicationReviewService.getReviewRequoteInfo(leadInfoId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          const requoteItems = response?.body || [];
          if (!Array.isArray(requoteItems) || requoteItems.length === 0) {
            this.showRequoteBanner = false;
            return;
          }

          // Use the latest requote entry
          const latest = requoteItems[requoteItems.length - 1];
          const status = (latest.requoteStatus || '').toLowerCase().trim();

          this.showRequoteBanner = true;

          if (status === 'declined') {
            this.requoteBannerTitle = 'Requote Declined';
            this.requoteBannerDescription = 'The agent has pushed back this request.';
            this.isRequoteDeclined = true;
          } else if (status === 'accepted') {
            this.showRequoteBanner = false;
          } else {
            // Pending or any other status
            this.requoteBannerTitle = 'Requote Requested';
            this.requoteBannerDescription = 'Awaiting response from the agent. The application is currently in view-only mode.';
            this.isRequoteDeclined = false;
          }
        },
        error: () => {
          this.showRequoteBanner = false;
        },
      });
  }

  openRequoteRemarkPopup(): void {
    this.showRequoteRemarkPopup = true;
  }

  closeRequoteRemarkPopup(): void {
    this.showRequoteRemarkPopup = false;
  }

  onMessage(): void {
    if (this.isViewMode) {
      return;
    }

    if (!this.userPermissionService.canShow(AppAction.ManageApplicationReview)) {
      return;
    }

    this.showRemarkPopup = true;
    this.remarksForm.reset({ remarks: '' });
  }

  closeRemarkPopup(): void {
    this.showRemarkPopup = false;
    this.remarksForm.reset({ remarks: '' });
  }

  handleRemarkSubmit(event: any): void {
    const remarks = (event?.remarks || '').trim();

    if (!remarks) {
      return;
    }

    this.isSavingRemarks = true;

    const payload = new FormData();
    payload.set(
      'leadInfoId',
      this.selectedLeadData?.id || this.selectedLeadData?.leadInfoId || '',
    );
    payload.set('activityType', 'Remarks');
    payload.set('noteContent', remarks);

    this.leadService
      .saveActivity(payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          this.isSavingRemarks = false;
          this.toaster.show({
            message:
              response?.messages?.[0] ||
              response?.message ||
              'Remarks saved successfully',
            status: 'success',
            position: 'top-right',
          });
          this.requoteRemarkRefreshKey++;
          this.closeRemarkPopup();
        },
        error: () => {
          this.isSavingRemarks = false;
        },
      });
  }

  onMarkAsReady(): void {
    if (this.isViewMode) {
      return;
    }

    if (!this.userPermissionService.canShow(AppAction.ManageApplicationReview)) {
      return;
    }

    this.showMarkReadyConfirmPopup = true;
  }

  onConfirmMarkAsReady(): void {
    const payload = this.buildSubmitApplicationReadyPayload();

    if (!payload) {
      this.toaster.show({
        message: 'Unable to mark application ready. Missing review details. Please refresh and try again.',
        status: 'error',
      });
      return;
    }

    const agentId = String(
      this.appReviewData?.leadInfo?.ownerAgentId ??
      this.appReviewData?.leadInfo?.ownerUserMaster?.id ??
      this.appReviewData?.headerSummary?.assignedAgentId ??
      this.selectedLeadData?.agentId ??
      this.selectedLeadData?.ownerAgentId ??
      ''
    ).trim();

    const doSubmit = (agentDetails: any) => {
      if (agentDetails) {
        payload.agent = agentDetails;
        if (payload.leadInfo) {
          payload.leadInfo.ownerUserMaster = agentDetails;
        }
      }

      this.ApplicationReviewService.subscribeWithCleanup(
        this.ApplicationReviewService.submitApplicationReady(payload),
        (response) => {
          this.showMarkReadyConfirmPopup = false;
          this.toaster.show({
            message: (response as any)?.messages?.[0] ?? `Lead ${this.leadInfoData.leadId} marked as application ready.`,
            status: 'success',
          });
          this.routeMode = 'view';
          this.reviewQueueSharedService?.setLeadMode?.('view');
          this.selectedLeadReviewStatus = 'Completed';
          if (this.selectedLeadData) {
            this.selectedLeadData.review_Status = ReviewStatus.Completed;
            this.selectedLeadData.status = ReviewStatus.Completed;
            this.reviewQueueSharedService?.setLeadData?.(this.selectedLeadData);
          }
          this.applyReviewStatus('Review Completed');
          const currentLeadInfoId = this.selectedLeadData?.leadInfoId ?? this.selectedLeadData?.id ?? '';
          if (currentLeadInfoId) {
            this.appReviewApiService?.invalidateLeadCaches?.(currentLeadInfoId);
            this.appReviewApiService?.invalidateMafCaches?.(currentLeadInfoId);
          }
          this.FetchApplicationReviewData(true);
        },
        this.destroyRef,
      );
    };

    if (agentId) {
      this.ApplicationReviewService.subscribeWithCleanup(
        this.ApplicationReviewService.getUserDetailsById(agentId),
        (userRes: any) => {
          const agentDetails = userRes?.body ?? userRes;
          doSubmit(agentDetails);
        },
        this.destroyRef,
      );
    } else {
      doSubmit(payload.agent);
    }
  }

  closeMarkAsReadyPopup(): void {
    this.showMarkReadyConfirmPopup = false;
  }

  private buildSubmitApplicationReadyPayload(): SubmitApplicationReadyPayload | null {
    const reviewQueueId = this.appReviewData?.reviewQueueId ?? this.selectedLeadData?.reviewQueueId ?? '';
    const leadInfoId = this.appReviewData?.leadInfoId ?? this.selectedLeadData?.leadInfoId ?? this.selectedLeadData?.id ?? '';
    const version = this.appReviewData?.version ?? this.appReviewData?.quoteVersion ?? this.appReviewData?.headerSummary?.quoteVersion ?? this.selectedLeadData?.quoteVersion ?? '';

    if (!reviewQueueId || !leadInfoId) {
      return null;
    }

    const leadInfo = this.appReviewData?.leadInfo ? { ...this.appReviewData.leadInfo } : null;

    const maf = this.appReviewData?.maf;
    const mafUploaded = this.appReviewData?.mafInfo?.mafUploadedDocuments;
    const rootMafConfigurationId =
      maf?.mafConfigurationId ??
      mafUploaded?.mafConfigurationId ??
      this.appReviewData?.mafInfo?.mafConfigurationId ??
      this.appReviewData?.headerSummary?.mafConfigurationId ??
      this.selectedLeadData?.mafConfigurationId ??
      '';

    const rawSubmissions = Array.isArray(maf?.submissions)
      ? maf.submissions
      : (Array.isArray(mafUploaded?.submissions) ? mafUploaded.submissions : []);

    const submissions = rawSubmissions.map((sub: any) => ({
      ...sub,
      mafConfigurationId: sub?.mafConfigurationId || rootMafConfigurationId || '',
    }));

    const completionStatus = {
      isOnlineForm: maf?.isOnlineForm ?? this.appReviewData?.mafInfo?.isOnlineForm ?? false,
      mafFinalSubmissionId: maf?.mafFinalSubmissionId ?? this.appReviewData?.mafInfo?.mafFinalSubmissionId ?? '',
      submissionStatus: maf?.submissionStatus ?? this.appReviewData?.mafInfo?.submissionStatus ?? 'ApplicationSubmitted',
      completionDate: maf?.completionDate ?? this.appReviewData?.mafInfo?.completionDate ?? null,
      status: maf?.status ?? mafUploaded?.status ?? 'Active',
      applicationReferenceNo: maf?.applicationReferenceNo ?? this.appReviewData?.mafInfo?.applicationReferenceNo ?? '',
      clusterId: maf?.clusterId ?? this.appReviewData?.mafInfo?.clusterId ?? '',
      mafConfigurationId: rootMafConfigurationId || (submissions[0]?.mafConfigurationId ?? ''),
      submissions,
    };

    const totalPremiumWithTaxes = Number(
      this.appReviewData?.latestQuote?.totalPremiumWithTaxes ??
      this.appReviewData?.totalPremiumWithTaxes ??
      0,
    );

    const product = this.appReviewData?.catalog ?? {
      insurer: { id: this.appReviewData?.headerSummary?.insurerId ?? '', name: this.appReviewData?.headerSummary?.insurerName ?? '' },
      product: { id: this.appReviewData?.headerSummary?.productId ?? '', name: this.appReviewData?.headerSummary?.productName ?? '' },
      plan: { id: this.appReviewData?.headerSummary?.planId ?? '', name: this.appReviewData?.headerSummary?.planName ?? '' },
      tpa: { id: '', name: '' },
      network: { id: this.appReviewData?.headerSummary?.networkId ?? '', name: this.appReviewData?.headerSummary?.networkName ?? '' },
      coverageArea: { id: '', name: '' },
      coPayType: { id: '', name: '' },
      paymentFrequency: { id: '', name: '' },
      currency: { currencyId: null, currencyCode: 'AED' },
    };

    const agent = leadInfo?.ownerUserMaster ?? null;

    const rawApplicants: any[] = Array.isArray(this.appReviewData?.applicants)
      ? this.appReviewData.applicants
      : (Array.isArray(this.applicantTabs) ? this.applicantTabs : []);

    const applicants = rawApplicants.map((app: any) => {
      const applicantId = String(app?.id ?? app?.applicantId ?? '').trim();
      const applicantName = String(
        app?.fullName ??
        `${app?.firstName ?? ''} ${app?.lastName ?? ''}`.trim() ??
        app?.label ??
        '',
      ).trim();
      return { applicantId, applicantName };
    }).filter((a: any) => !!a.applicantId);

    return {
      leadInfoId,
      reviewQueueId,
      version,
      leadInfo,
      completionStatus,
      totalPremiumWithTaxes,
      product,
      agent,
      applicants,
    };
  }

  closeReopenApplicantPopup(): void {
    this.showReopenApplicantConfirmPopup = false;
  }

  get reopenApplicantConfirmMessage(): string {
    const selectedApplicant = this.applicantTabs[this.applicantSelectedIndex]?.label?.trim();
    const fallbackApplicant = this.leadInfoData.clientName?.trim();
    const applicantName = this.escapeHtml(selectedApplicant || fallbackApplicant || 'this applicant');

    return `This will return <strong>${applicantName}</strong> to a pending status. All documents and data fields will be unlocked for further editing and verification.<br><br>Are you sure you want to proceed?`;
  }

  onToggleApplicantReviewed(): void {
    if (this.isViewMode) {
      return;
    }

    const applicantIndex = this.applicantSelectedIndex;
    if (
      applicantIndex < 0 ||
      applicantIndex >= this.applicantReviewedStates.length
    ) {
      return;
    }

    const isCurrentlyReviewed = this.applicantReviewedStates[applicantIndex];
    if (isCurrentlyReviewed) {
      this.showReopenApplicantConfirmPopup = true;
      return;
    }

    this.updateApplicantReviewStatus(ReviewActionType.MARK_APPLICANT, 'Applicant review status updated.');
  }

  onConfirmReopenApplicantReview(): void {
    this.showReopenApplicantConfirmPopup = false;
    this.updateApplicantReviewStatus(ReviewActionType.UNMARK_APPLICANT, 'Applicant review status updated.');
  }

  private resolveMafFormSubmissionIdForApplicant(applicantId: string | null | undefined): string {
    const targetId = String(applicantId ?? '').trim();
    if (!targetId) return '';

    const submissions = this.appReviewData?.mafInfo?.mafUploadedDocuments?.submissions
      ?? this.appReviewData?.maf?.submissions
      ?? [];

    for (const sub of submissions) {
      const applicants = Array.isArray(sub?.applicants) ? sub.applicants : [];
      const matched = applicants.some(
        (a: any) => String(a?.applicantId ?? a?.id ?? '').trim() === targetId,
      );
      if (matched && sub?.mafFormSubmissionId) {
        return String(sub.mafFormSubmissionId).trim();
      }
    }

    const applicantReview = (this.appReviewData?.applicantReviews ?? []).find(
      (r: any) => String(r?.applicantId ?? '').trim() === targetId,
    );
    if (applicantReview?.['mafFormSubmissionId']) {
      return String(applicantReview['mafFormSubmissionId']).trim();
    }

    return '';
  }

  private updateApplicantReviewStatus(actionType: ReviewActionType, fallbackMessage: string): void {
    const applicantInfoId = this.applicantTabs[this.applicantSelectedIndex]?.applicantId;
    const reviewQueueId = this.appReviewData?.reviewQueueId;

    if (!applicantInfoId || !reviewQueueId) {
      return;
    }

    const mafFormSubmissionId = this.resolveMafFormSubmissionIdForApplicant(applicantInfoId);

    const payload: MarkOrUnmarkPayload = {
      reviewQueueId,
      mafFormSubmissionId,
      applicantInfoId,
      actionType,
    };

    this.ApplicationReviewService.subscribeWithCleanup(
      this.ApplicationReviewService.markOrUnmarkApplicationReview(payload),
      (response: any) => {
        this.toaster.show({
          message: response?.messages ?? fallbackMessage,
          status: 'success',
          position: 'top-right',
        });
        this.FetchApplicationReviewData(true);
      },
      this.destroyRef,
    );
  }

  private escapeHtml(value: string): string {
    return value
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }

  onProceedToNextApplicant(): void {
    if (this.isViewMode) {
      return;
    }

    const nextIndex = this.applicantSelectedIndex + 1;
    if (nextIndex >= this.applicantTabs.length) {
      return;
    }

    this.onApplicantTabChange({ index: nextIndex });
  }

  canNavigateToPreviousApplicant(): boolean {
    return this.applicantSelectedIndex > 0;
  }

  canNavigateToNextApplicant(): boolean {
    return this.applicantSelectedIndex < this.applicantTabs.length - 1;
  }

  onPreviousApplicant(): void {
    if (!this.canNavigateToPreviousApplicant()) {
      return;
    }

    this.onApplicantTabChange({ index: this.applicantSelectedIndex - 1 });
  }

  onNextApplicant(): void {
    if (!this.canNavigateToNextApplicant()) {
      return;
    }

    this.onApplicantTabChange({ index: this.applicantSelectedIndex + 1 });
  }

  onApplicantTabChange(event: { index: number }): void {
    if (event.index === this.applicantSelectedIndex) {
      return;
    }

    // Apply tab change directly without unsaved changes check for internal navigation
    // Data is retained and unsaved changes popup only appears on true route exit (canDeactivate)
    this.applyApplicantTabChange(event.index);
  }

  onMafVerificationChanged(isVerified: boolean): void {
    this.ngZone.run(() => {
      const applicantId = this.applicantTabs[this.applicantSelectedIndex]?.applicantId;
      if (!applicantId) {
        return;
      }

      this.setMafVerificationFlag(applicantId, isVerified);

      if (this.isOfflineMafMode()) {
        const primaryApplicantId = this.getPrimaryApplicantIdForMaf();
        if (primaryApplicantId && applicantId === primaryApplicantId) {
          const sharedDependentApplicantIds = this.getSharedDependentApplicantIds();
          sharedDependentApplicantIds.forEach((sharedApplicantId) => {
            this.setMafVerificationFlag(sharedApplicantId, isVerified);
          });
        }
      }

      this.syncVerifyDetailsTabIconsForSelectedApplicant();
      this.cdr.detectChanges();
    });
  }

  onMafSharedApplicantsVerificationUpdated(event: { applicantIds: string[]; isVerified: boolean }): void {
    this.ngZone.run(() => {
      const applicantIds = event?.applicantIds ?? [];

      applicantIds.forEach((applicantId) => {
        if (!applicantId) {
          return;
        }

        this.setMafVerificationFlag(applicantId, event.isVerified);
      });

      this.syncVerifyDetailsTabIconsForSelectedApplicant();
      this.cdr.detectChanges();
    });
  }

  onVerifyTabChange(event: { index: number }): void {
    if (event.index === this.verifySelectedIndex) {
      return;
    }

    // Apply tab change directly without unsaved changes check for internal navigation
    // Data is retained and unsaved changes popup only appears on true route exit (canDeactivate)
    this.applyVerifyTabChange(event.index);
  }

  onDocumentsFinalizedChanged(_isFinalized: boolean): void {
    this.updateVerifyFlagsForAllApplicants();
    this.syncVerifyDetailsTabIconsForSelectedApplicant();
  }

  onDocumentReviewStatusRefreshRequested(payload?: string | { message?: string; delayMs?: number }): void {
    const message = typeof payload === 'string' ? payload : payload?.message ?? 'Document changes saved successfully.';
    const delayMs = typeof payload === 'object' ? payload.delayMs ?? 5000 : 5000;
    this.queuePostSaveRefresh(message, delayMs);
  }

  private queuePostSaveRefresh(successMessage: string, delayMs = 5000): void {
    const leadInfoId = this.selectedLeadData?.leadInfoId ?? this.selectedLeadData?.id ?? '';
    this.appReviewApiService?.invalidateLeadCaches(leadInfoId);
    this.appReviewApiService?.invalidateMafCaches(leadInfoId);

    if (this.postSaveRefreshTimer) {
      this.postSaveSuccessMessage = successMessage;
      return;
    }

    this.loaderService.show();
    this.postSaveSuccessMessage = successMessage;
    this.postSaveRefreshTimer = setTimeout(() => {
      this.postSaveRefreshTimer = null;
      this.FetchApplicationReviewData(true);
    }, delayMs);
  }

  get reviewedApplicantsCount(): number {
    return this.applicantReviewedStates.filter(Boolean).length;
  }

  get totalApplicantsCount(): number {
    return this.applicantTabs.length;
  }

  get isCurrentApplicantReviewed(): boolean {
    return !!this.applicantReviewedStates[this.applicantSelectedIndex];
  }

  get isDocumentsTabWarning(): boolean {
    return !this.getSelectedApplicantDocumentReviewed();
  }

  get isOverallSaveDisabled(): boolean {
    if (this.applicantTabs.length === 0) {
      return false;
    }

    return this.applicantTabs.every((tab) => {
      const applicantId = tab.applicantId;
      return !!applicantId && this.hasDocumentsReviewedForApplicant(applicantId);
    });
  }

  get isApplicationReady(): boolean {
    return (
      this.totalApplicantsCount > 0 &&
      this.reviewedApplicantsCount === this.totalApplicantsCount
    );
  }

  get isCurrentApplicantEligibleForReviewMark(): boolean {
    return this.isApplicantVerified && this.isMafVerified && this.isDocumentsVerified;
  }

  get canProceedToNextApplicant(): boolean {
    return this.applicantSelectedIndex < this.applicantTabs.length - 1;
  }

  get isReviewCompletedViewMode(): boolean {
    return this.isViewMode && (
      this.statusLabel === 'Review Completed' ||
      this.statusLabel === 'Payment Follow-up' ||
      this.selectedLeadReviewStatus === 'Completed' ||
      this.selectedLeadReviewStatus === 'PaymentFollowUp'
    );
  }

  get isCopyModeEnabled(): boolean {
    // Enable copy mode for read-only view states and review mode so submitted values can be copied.
    return this.isViewMode || this.isReviewCompletedViewMode || (this.reviewMode && !this.isViewMode);
  }

  get canAssignInViewMode(): boolean {
    return this.isViewMode && this.statusLabel === 'Unassigned' && this.userPermissionService.canShow(AppAction.LeadReviewer);
  }

  get quotePreviewProductId(): string {
    return (
      this.appReviewData?.quoteDetails?.selectedQuoteProductId ??
      this.appReviewData?.productId ??
      this.selectedLeadData?.productId ??
      ''
    );
  }

  get quotePreviewLeadInfoId(): string {
    return (
      this.appReviewData?.leadInfoId ??
      this.selectedLeadData?.leadInfoId ??
      this.selectedLeadData?.id ??
      ''
    );
  }

  private openReviewerPopup(action: ReviewAssignmentAction): void {
    this.showAssignPopup = action === 'assign';
    this.showReassignPopup = action === 'reassign';
    this.popupConfig = this.buildReviewerPopupConfig(action);
    this.loadReviewerOptions();
  }

  private buildReviewerPopupConfig(
    action: ReviewAssignmentAction,
  ): FormPopupConfig {
    const selectedStatus = this.selectedLeadReviewStatus;

    const reviewStatus = selectedStatus
      ? this.reviewStatusMap[selectedStatus]
      : ReviewStatus.Unassigned;

    const isAssign =
      action === 'assign' || reviewStatus === ReviewStatus.Unassigned;
    const fullName = [
      this.assignmentLeadContext?.firstName,
      this.assignmentLeadContext?.lastName,
    ]
      .filter((name): name is string => Boolean(name && name.trim()))
      .join(' ');
    const leadId =
      this.assignmentLeadContext?.leadId ?? this.leadInfoData.leadId;
    const clientName = fullName || this.leadInfoData.clientName;

    return {
      header: {
        iconName: 'icon-person',
        subtitle: isAssign ? 'Assign' : 'Reassign',
        title: 'Reviewer',
      },
      selectedInfo: isAssign
        ? {
          'Lead ID': leadId,
          'Client Name': clientName,
        }
        : {
          'Lead ID': leadId,
          'Current Reviewer': this.leadInfoData.assignedReviewer,
        },
      fields: [
        {
          name: 'reviewerId',
          label: isAssign ? 'Reviewer to Assign' : 'Reviewer to Reassign',
          type: 'dropdown',
          dropdownOptions: this.assignmentReviewerOptions,
          searchable: true,
          searchHandler: (query: string) => this.loadReviewerOptions(query),
          validators: { required: true },
        },
        ...(!isAssign
          ? [
            {
              name: 'reassignedReason',
              label: 'Reason for reassignment',
              type: 'textarea' as const,
              placeholder: 'Enter reason',
              validators: { maxLength: 200 },
            },
          ]
          : []),
      ],
      action: {
        submitLabel: isAssign ? 'Assign' : 'Reassign',
      },
    };
  }

  private buildReviewerAssignmentPayload(
    reviewerId: string,
    reassignedReason: string | null,
  ): ReviewerAssignmentPayload {
    const selectedStatus = this.selectedLeadReviewStatus;

    const CurrentreviewStatus = selectedStatus
      ? this.reviewStatusMap[selectedStatus]
      : ReviewStatus.Unassigned;
    const reviewStatus = ReviewStatus.Assigned;
    const isReassigned = CurrentreviewStatus !== ReviewStatus.Unassigned;

    const leadData = this.selectedLeadData;
    const lead: ReviewerAssignmentLead = {
      reviewQueueId: leadData?.reviewQueueId ?? '',
      leadInfoId: leadData?.leadInfoId ?? leadData?.id ?? '',
      mafFinalSubmissionId: this.appReviewData?.mafInfo?.mafFinalSubmissionId ?? leadData?.mafFinalSubmissionId ?? null,
      assignedAgentId: leadData?.assignedAgentId ?? null,
    };

    return isReassigned ? {
      reviewerId,
      reviewStatus,
      isReassigned,
      leads: [lead],
      reassignedReason: reassignedReason,
    }
      : {
        reviewerId,
        reviewStatus,
        leads: [lead],
        isReassigned: isReassigned,
      };
  }

  private getReviewerLabel(reviewerId: string): string {
    return (
      this.assignmentReviewerOptions.find(
        (option) => option.value === reviewerId,
      )?.label ?? reviewerId
    );
  }

  private loadReviewerOptions(query = ''): void {
    this.reviewQueueService.subscribeWithCleanup(
      this.reviewQueueService.getReviewerDropdown(query),
      (response: any) => {
        const options = this.mapReviewerOptions(response);
        this.assignmentReviewerOptions = options;
        this.refreshReviewerDropdownOptions();
        this.resolveMissingNames();
      },
      this.destroyRef,
    );
  }

  private loadAssignedAgentOptions(): void {
    if (typeof this.reviewQueueService?.getUserAgentDD !== 'function') {
      return;
    }

    const payload: UserAgentDropDownPayload = {
      agentId: agentObjectSignal()?.agentId ?? '',
      insuranceCategory: 'RetailHealth',
    };

    this.reviewQueueService.subscribeWithCleanup(
      this.reviewQueueService.getUserAgentDD(payload),
      (response: any) => {
        if (!response?.body) return;
        const userAgentList = Array.isArray(response.body)
          ? response.body
          : response.body?.userAgent;
        if (!Array.isArray(userAgentList)) {
          this.assignedAgentOptions = [];
          return;
        }
        this.assignedAgentOptions = this.mapAssignedAgentOptions(response);
        this.resolveMissingNames();
      },
      this.destroyRef,
    );
  }

  private mapAssignedAgentOptions(response: any): DropdownOption[] {
    const body = response?.body ?? {};
    const agentList = Array.isArray(body) ? body : body.userAgent ?? [];
    return (Array.isArray(agentList) ? agentList : [])
      .map((item: any): DropdownOption => {
        const mappedItem = item as { id?: unknown; name?: unknown };
        const value = mappedItem.id;
        const label = mappedItem.name;
        let normalizedValue = '';

        if (typeof value === 'string') {
          normalizedValue = value;
        } else if (typeof value === 'number') {
          normalizedValue = value.toString();
        }

        return {
          value: normalizedValue,
          label: typeof label === 'string' ? label : '',
        };
      })
      .filter((option: DropdownOption) => !!option.value && !!option.label);
  }

  private resolveMissingNames(): void {
    let changed = false;
    let assignedReviewer = this.leadInfoData.assignedReviewer;
    let assignedAgent = this.leadInfoData.assignedAgent;

    const reviewerId = String(
      this.selectedLeadData?.reviewerId ??
      this.selectedLeadData?.reviewerAssignedId ??
      this.appReviewData?.headerSummary?.assignedReviewerId ??
      this.appReviewData?.reviewQueue?.assignedReviewerId ??
      ''
    ).trim();

    if ((!assignedReviewer || assignedReviewer === '-' || assignedReviewer.trim() === '') && reviewerId) {
      const found = this.assignmentReviewerOptions.find(
        (opt) => String(opt.value).toLowerCase() === reviewerId.toLowerCase()
      );
      if (found?.label) {
        assignedReviewer = found.label;
        changed = true;
      }
    }

    const agentId = String(
      this.selectedLeadData?.assignedAgentId ??
      this.selectedLeadData?.agentId ??
      this.selectedLeadData?.ownerAgentId ??
      this.appReviewData?.headerSummary?.assignedAgentId ??
      ''
    ).trim();

    if ((!assignedAgent || assignedAgent === '-' || assignedAgent.trim() === '') && agentId) {
      const found = this.assignedAgentOptions.find(
        (opt) => String(opt.value).toLowerCase() === agentId.toLowerCase()
      );
      if (found?.label) {
        assignedAgent = found.label;
        changed = true;
      }
    }

    if (changed) {
      this.leadInfoData = {
        ...this.leadInfoData,
        assignedReviewer,
        assignedAgent,
      };
      this.cdr.detectChanges();
    }
  }

  private readonly userService = inject(UserService);

  private mapReviewerOptions(response: any): DropdownOption[] {
    const body = response?.body ?? {};
    const reviewerList = body.userAgent ?? body.reviewer ?? [];
    const loggedInAgentId = this.normalizeReviewerId(
      currentUserProfileSignal()?.id ??
      this.userService.getAgentId() ??
      agentObjectSignal()?.agentId,
    );

    return (Array.isArray(reviewerList) ? reviewerList : [])
      .map((item: any): DropdownOption => {
        const value = item?.id;
        const label = item?.name;
        const optionValue = value !== undefined && value !== null ? String(value) : '';
        const optionLabel = typeof label === 'string' ? label : '';
        const isLoggedInAgent =
          !!loggedInAgentId &&
          this.normalizeReviewerId(optionValue) === loggedInAgentId;

        return {
          value: optionValue,
          label: isLoggedInAgent ? `${optionLabel.trim()} (Self)` : optionLabel,
        };
      })
      .filter((option) => !!option.value && !!option.label);
  }

  private normalizeReviewerId(value: unknown): string {
    return typeof value === 'string' ? value.trim().toLowerCase() : '';
  }

  private refreshReviewerDropdownOptions(): void {
    this.popupConfig = {
      ...this.popupConfig,
      fields: this.popupConfig.fields.map((field) =>
        field.name === 'reviewerId'
          ? {
            ...field,
            dropdownOptions: this.assignmentReviewerOptions,
            searchHandler: (query: string) => this.loadReviewerOptions(query),
          }
          : field,
      ),
    };
  }

  private getAssignmentSuccessMessage(
    response: unknown,
    action: ReviewAssignmentAction,
  ): string {
    if (
      typeof response === 'object' &&
      response !== null &&
      'message' in response
    ) {
      const messageValue = (response as { message?: unknown }).message;
      if (typeof messageValue === 'string' && messageValue.trim()) {
        return messageValue;
      }
    }

    return action === 'assign'
      ? 'Reviewer assigned successfully.'
      : 'Reviewer reassigned successfully.';
  }

  private getValidReviewStatus(status: unknown): ReviewAssignmentStatus | null {
    const allowedStatuses: ReviewAssignmentStatus[] = [
      'Unassigned',
      'Assigned',
      'Review In-Progress',
      'Agent Follow-up',
      'Requote Requested',
      'Review Completed',
    ];

    if (typeof status === 'string') {
      const normalizedStatus = status.trim();
      if (!normalizedStatus) {
        return null;
      }

      if (allowedStatuses.includes(normalizedStatus as ReviewAssignmentStatus)) {
        return normalizedStatus as ReviewAssignmentStatus;
      }
    }

    const reviewTab = this.resolveReviewStatusTab(status);
    return reviewTab ? this.mapReviewTabToStatusLabel(reviewTab) : null;
  }

  private applyReviewStatus(status: ReviewAssignmentStatus): void {
    this.statusLabel = status;

    switch (status) {
      case 'Unassigned':
        this.isUnassigned = true;
        this.statusBgColor = '#DDE2EB';
        this.statusTextColor = '#4E5A6E';
        break;
      case 'Assigned':
        this.isUnassigned = false;
        this.statusBgColor = '#E1EEFF';
        this.statusTextColor = '#1945A7';
        break;
      case 'Review In-Progress':
        this.isUnassigned = false;
        this.statusBgColor = '#FFEAAE';
        this.statusTextColor = '#7C5111';
        break;
      case 'Agent Follow-up':
        if (this.routeMode === 'requote') {
          this.statusLabel = 'Requote Requested';
          this.statusBgColor = '#FFD5E6';
          this.statusTextColor = '#92194B';
          this.fetchRequoteBannerInfo();
        } else {
          this.statusBgColor = '#FFE5EA';
          this.statusTextColor = '#CC1313';
        }
        this.isUnassigned = false;
        break;
      case 'Requote Requested':
        this.isUnassigned = false;
        this.statusBgColor = '#FFD5E6';
        this.statusTextColor = '#92194B';
        this.fetchRequoteBannerInfo();
        break;
      case 'Review Completed':
        this.isUnassigned = false;
        this.statusBgColor = '#DFFFF1';
        this.statusTextColor = '#236F3E';
        break;
    }

    this.syncModeFromRoute();
  }

  private resolveRouteMode(mode: unknown): 'view' | 'review' | 'requote' | null {
    if (typeof mode !== 'string') {
      return null;
    }

    const normalizedMode = mode.trim().toLowerCase();
    if (normalizedMode === 'view' || normalizedMode === 'review' || normalizedMode === 'requote') {
      return normalizedMode;
    }

    return null;
  }

  private syncModeFromRoute(): void {
    if (
      this.statusLabel === 'Review Completed' ||
      this.statusLabel === 'Payment Follow-up' ||
      this.selectedLeadReviewStatus === 'Completed' ||
      this.selectedLeadReviewStatus === 'PaymentFollowUp'
    ) {
      this.isViewMode = true;
      this.reviewMode = false;
      return;
    }

    if (this.routeMode) {
      this.isViewMode = this.routeMode === 'view' || this.routeMode === 'requote';
      this.reviewMode = this.routeMode === 'review';
    } else {
      // Backward compatibility: old URLs without mode treat unassigned as view-only.
      this.isViewMode = this.isUnassigned;
      this.reviewMode = !this.isViewMode;
    }

    if (!this.userPermissionService.canShow(AppAction.ManageApplicationReview)) {
      this.isViewMode = true;
      this.reviewMode = false;
    }

    if (
      this.statusLabel === 'Unassigned' &&
      !this.userPermissionService.canShow(AppAction.LeadReviewer) &&
      !this.userPermissionService.hasPermission('LeadInfo.ViewAdmin')
    ) {
      this.isViewMode = true;
      this.reviewMode = false;
      void this.router.navigate(['/service-unavailable']);
    }
  }

  FetchApplicationReviewData(forceRefresh = false): void {
    const leadId = this.selectedLeadData?.lead_Id ?? this.selectedLeadData?.id ?? '';
    const productId = this.selectedLeadData?.productId ?? '';
    const leadInfoId = this.selectedLeadData?.leadInfoId ?? this.selectedLeadData?.id ?? '';
    if (!leadId) return;
    this.ApplicationReviewService.subscribeWithCleanup(
      this.ApplicationReviewService.getApplicationReviewData(leadId, productId, leadInfoId, this.selectedLeadData, forceRefresh),
      (res) => {
        const applicationData = res.body ?? {};
        this.leadInfoData = this.mapHeaderSummaryToLeadInfoData(applicationData.headerSummary);

        if (applicationData.reviewQueue) {
          const rq = applicationData.reviewQueue;
          const rawLastActivity = String(rq.lastActivity ?? applicationData.headerSummary?.lastActivity ?? '').trim();
          if (rawLastActivity) {
            this.leadInfoData = {
              ...this.leadInfoData,
              lastActivityText: rawLastActivity,
              lastActivityTime: '',
            };
          }
        }
        this.resolveMissingNames();
        this.appReviewData = {
          ...res.body,
          leadInfoId: this.selectedLeadData?.leadInfoId,
          leadId: this.selectedLeadData?.lead_Id,
          contactName: this.selectedLeadData?.contactName,
          reviewQueueId: this.selectedLeadData?.reviewQueueId,
          quoteVersion: this.selectedLeadData?.quoteVersion,
          productId: this.selectedLeadData?.productId,
        };

        this.reviewQueueSharedService.setApplicationReviewData(this.appReviewData);

        if (this.postSaveSuccessMessage) {
          this.toaster.show({
            message: this.postSaveSuccessMessage,
            status: 'success',
            position: 'top-right',
          });
          this.postSaveSuccessMessage = null;
          this.loaderService.hide();
        }

        const savedDate = res.body?.savedDate ?? applicationData.savedDate;
        if (savedDate) {
          this.updateLastSavedTime(savedDate);
        }

        const applicantInfo = applicationData.applicantInfo ?? null;
        this.applicantProfileData = applicantInfo
          ? { ...applicantInfo, preferences: applicantInfo.preferences ?? applicantInfo.preference ?? {} }
          : null;

        const reviewStatus = this.getValidReviewStatus(applicationData.reviewQueue?.reviewStatus);
        const effectiveStatus = (this.routeMode === 'view' && this.selectedLeadReviewStatus === 'Completed')
          ? 'Review Completed'
          : (reviewStatus ?? this.statusLabel);
        this.applyReviewStatus(effectiveStatus);

        const targetLeadInfoId = applicationData.headerSummary?.leadInfoId || leadInfoId;

        if (!this.applicantProfileData?.primaryApplicant && targetLeadInfoId) {
          this.fetchApplicantsFromApi(targetLeadInfoId, applicationData);
        } else {
          this.applyEmirateNamesToApplicants();
          this.buildApplicantTabs();
          this.deriveApplicantReviewedStates();
          this.updateVerifyFlagsForAllApplicants();
          this.syncVerifyDetailsTabIconsForSelectedApplicant();
        }
      },
      this.destroyRef,
    );
  }

  onAdditionalInfoSaved(updatedAdditionalInfo?: Record<string, unknown>): void {
    if (this.applicantProfileData && updatedAdditionalInfo) {
      this.applicantProfileData = {
        ...this.applicantProfileData,
        additionalInfo: {
          ...this.applicantProfileData.additionalInfo,
          ...updatedAdditionalInfo,
        },
      };
    }

    this.queuePostSaveRefresh('Additional information saved successfully.', 5000);
  }

  private loadEmiratesDropdown(): void {
    this.ApplicationReviewService.subscribeWithCleanup(
      this.ApplicationReviewService.getEmiratesDropdown(),
      (res) => {
        const list: any[] = Array.isArray(res?.body) ? res.body : [];
        const map: Record<string, string> = {};
        for (const item of list) {
          if (item?.id && (item?.emirateName || item?.name)) {
            map[String(item.id).toLowerCase()] = String(item.emirateName || item.name).trim();
          }
        }
        this.emiratesMap = map;
        this.applyEmirateNamesToApplicants();
      },
      this.destroyRef,
    );
  }

  private getEmirateNameById(emirateId: string | null | undefined): string {
    if (!emirateId) return '';
    return this.emiratesMap[String(emirateId).toLowerCase()] ?? '';
  }

  private applyEmirateNamesToApplicants(): void {
    if (!this.applicantProfileData) return;
    let changed = false;

    if (this.applicantProfileData.primaryApplicant) {
      const primary = this.applicantProfileData.primaryApplicant;
      if (!primary.emirateOfVisaInsurance && primary.emirateOfVisaInsuranceId) {
        const mappedName = this.getEmirateNameById(primary.emirateOfVisaInsuranceId);
        if (mappedName) {
          this.applicantProfileData = {
            ...this.applicantProfileData,
            primaryApplicant: {
              ...primary,
              emirateOfVisaInsurance: mappedName,
            },
          };
          changed = true;
        }
      }
    }

    if (Array.isArray(this.applicantProfileData.dependentApplicants)) {
      const deps = this.applicantProfileData.dependentApplicants.map((dep) => {
        if (!dep.emirateOfVisaInsurance && dep.emirateOfVisaInsuranceId) {
          const mappedName = this.getEmirateNameById(dep.emirateOfVisaInsuranceId);
          if (mappedName) {
            changed = true;
            return { ...dep, emirateOfVisaInsurance: mappedName };
          }
        }
        return dep;
      });

      if (changed) {
        this.applicantProfileData = {
          ...this.applicantProfileData,
          dependentApplicants: deps,
        };
      }
    }

    if (changed) {
      this.cdr.detectChanges();
    }
  }

  private fetchApplicantsFromApi(leadInfoId: string, applicationData: any): void {
    this.ApplicationReviewService.subscribeWithCleanup(
      this.ApplicationReviewService.getApplicants(leadInfoId),
      (applicantsRes) => {
        const rawApplicants: any[] = Array.isArray(applicantsRes?.body)
          ? applicantsRes.body
          : [];

        if (rawApplicants.length > 0) {
          const resolveEmirateName = (raw: any): string => {
            return raw.emirateOfVisaMaster?.name ??
              raw.emirateOfVisaMaster?.emirateName ??
              raw.emirateOfVisaInsurance ??
              this.getEmirateNameById(raw.emirateOfVisaInsuranceId);
          };

          const primaryRaw = rawApplicants.find((a: any) => a.isPrimaryApplicant) ?? rawApplicants[0];
          const primaryApplicant: ApplicantBasicInfoApi = {
            id: primaryRaw.id,
            firstName: primaryRaw.firstName ?? '',
            lastName: primaryRaw.lastName ?? '',
            dob: primaryRaw.dob ?? '',
            gender: primaryRaw.gender ?? '',
            maritalStatus: primaryRaw.maritalStatus ?? '',
            emirateOfVisaInsuranceId: primaryRaw.emirateOfVisaInsuranceId,
            emirateOfVisaInsurance: resolveEmirateName(primaryRaw),
            relationshipToSelf: primaryRaw.relationshipToSelf,
            passportInfoId: primaryRaw.passportInfoId,
            passportFileName: primaryRaw.passportFileName,
            passportFilePath: primaryRaw.passportFilePath,
            passportFileSize: primaryRaw.passportFileSize,
            passportFileType: primaryRaw.passportFileType,
          };

          const dependentApplicants: ApplicantBasicInfoApi[] = rawApplicants
            .filter((a: any) => a.id !== primaryRaw.id)
            .map((dep: any) => ({
              id: dep.id,
              firstName: dep.firstName ?? '',
              lastName: dep.lastName ?? '',
              dob: dep.dob ?? '',
              gender: dep.gender ?? '',
              maritalStatus: dep.maritalStatus ?? '',
              emirateOfVisaInsuranceId: dep.emirateOfVisaInsuranceId,
              emirateOfVisaInsurance: resolveEmirateName(dep),
              relationshipToSelf: dep.relationshipToSelf,
              passportInfoId: dep.passportInfoId,
              passportFileName: dep.passportFileName,
              passportFilePath: dep.passportFilePath,
              passportFileSize: dep.passportFileSize,
              passportFileType: dep.passportFileType,
            }));

          const additionalInfo = this.applicantProfileData?.additionalInfo ?? applicationData?.applicantInfo?.additionalInfo ?? {
            sponsorName: applicationData?.headerSummary?.contactName ?? '',
          };

          const isSelfSponsor =
            String(additionalInfo?.sponsor ?? '').trim().toLowerCase() === 'self' ||
            String(additionalInfo?.sponsoType ?? '').trim().toLowerCase() === 'self';
          if (isSelfSponsor && !String(additionalInfo?.sponsorName ?? '').trim()) {
            additionalInfo.sponsorName = `${primaryApplicant.firstName ?? ''} ${primaryApplicant.lastName ?? ''}`.trim();
          }

          this.applicantProfileData = {
            primaryApplicant,
            dependentApplicants,
            additionalInfo,
            preferences: this.applicantProfileData?.preferences ?? applicationData?.applicantInfo?.preferences ?? applicationData?.applicantInfo?.preference ?? {},
          };

          if (this.leadInfoData && (!this.leadInfoData.noOfApplicants || this.leadInfoData.noOfApplicants <= 0)) {
            this.leadInfoData.noOfApplicants = rawApplicants.length;
          }
        }

        this.applyEmirateNamesToApplicants();
        this.buildApplicantTabs();
        this.deriveApplicantReviewedStates();
        this.updateVerifyFlagsForAllApplicants();
        this.syncVerifyDetailsTabIconsForSelectedApplicant();
        this.cdr.detectChanges();
      },
      this.destroyRef,
    );
  }

  private formatLastActivityDisplay(
    rawLastActivity: unknown,
    rawLastActivityDate: unknown,
  ): { text: string; time: string } {
    let text = '';
    let time = '';

    const activityStr = typeof rawLastActivity === 'string' ? rawLastActivity.trim() : '';
    const dateStr = typeof rawLastActivityDate === 'string' ? rawLastActivityDate.trim() : '';

    if (activityStr) {
      const isDateString = activityStr.includes('T') || /^\d{4}-\d{2}-\d{2}/.test(activityStr);
      if (isDateString) {
        text = DateTimeConversionHelper.toDayMonthYear(activityStr) || activityStr;
        time = DateTimeConversionHelper.toTime12Hour(activityStr);
      } else {
        text = activityStr;
        if (dateStr) {
          time = DateTimeConversionHelper.toTime12Hour(dateStr);
        }
      }
    } else if (dateStr) {
      text = DateTimeConversionHelper.toDayMonthYear(dateStr) || dateStr;
      time = DateTimeConversionHelper.toTime12Hour(dateStr);
    }

    return { text, time };
  }

  private mapHeaderSummaryToLeadInfoData(headerSummary: any): LeadInfoData {
    if (!headerSummary) {
      return this.leadInfoData;
    }

    const clientName =
      headerSummary.contactName ??
      `${headerSummary.firstName ?? ''} ${headerSummary.lastName ?? ''}`.trim();

    let assignedReviewer = headerSummary.assignedReviewerName ?? this.leadInfoData.assignedReviewer ?? '';
    const reviewerId = String(
      headerSummary.assignedReviewerId ??
      this.selectedLeadData?.reviewerId ??
      this.selectedLeadData?.reviewerAssignedId ??
      this.appReviewData?.reviewQueue?.assignedReviewerId ??
      ''
    ).trim();
    if ((!assignedReviewer || assignedReviewer === '-' || assignedReviewer.trim() === '') && reviewerId) {
      const found = this.assignmentReviewerOptions.find(
        (opt) => String(opt.value).toLowerCase() === reviewerId.toLowerCase()
      );
      if (found?.label) {
        assignedReviewer = found.label;
      }
    }

    let assignedAgent = headerSummary.assignedAgentName ?? this.leadInfoData.assignedAgent ?? '';
    const agentId = String(
      headerSummary.assignedAgentId ??
      this.selectedLeadData?.assignedAgentId ??
      this.selectedLeadData?.agentId ??
      this.selectedLeadData?.ownerAgentId ??
      ''
    ).trim();
    if ((!assignedAgent || assignedAgent === '-' || assignedAgent.trim() === '') && agentId) {
      const found = this.assignedAgentOptions.find(
        (opt) => String(opt.value).toLowerCase() === agentId.toLowerCase()
      );
      if (found?.label) {
        assignedAgent = found.label;
      }
    }

    const rawLastActivity = String(headerSummary.lastActivity ?? this.selectedLeadData?.lastActivity ?? '').trim();
    const lastActivityText = rawLastActivity || this.leadInfoData.lastActivityText;
    const lastActivityTime = '';

    return {
      clientName,
      clientInitials: this.getInitials(headerSummary.firstName, headerSummary.lastName),
      leadId: headerSummary.leadId ?? this.leadInfoData.leadId,
      lastActivityText,
      lastActivityTime,
      quoteReference: headerSummary.quoteId ?? '',
      quoteVersion: headerSummary.quoteVersion ?? '',
      insurer: headerSummary.insurerName ?? '',
      planName: headerSummary.planName ?? '',
      network: headerSummary.networkName ?? '',
      leadCreatedOn: DateTimeConversionHelper.toDayMonthYear(headerSummary.createdOn) ?? '',
      noOfApplicants: headerSummary.numberOfApplicants ?? 0,
      assignedAgent,
      assignedReviewer,
    };
  }

  private updateLastSavedTime(savedDate: string): void {
    try {
      const date = new Date(savedDate);
      if (Number.isNaN(date.getTime())) {
        return; // Invalid date
      }

      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      let timeAgo = '';
      if (diffMins < 1) {
        timeAgo = 'just now';
      } else if (diffMins < 60) {
        timeAgo = `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
      } else if (diffHours < 24) {
        timeAgo = `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
      } else if (diffDays < 30) {
        timeAgo = `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
      } else {
        timeAgo = this.dateTimeHelper.toDayMonthYear(savedDate) ?? 'some time ago';
      }

      const timeString = this.dateTimeHelper.toTime12Hour(savedDate) ?? date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });

      this.lastSaved = `Saved ${timeAgo} (${timeString})`;
    } catch {
    }
  }

  buildApplicantTabs(): void {
    if (!this.applicantProfileData?.primaryApplicant) {
      this.applicantTabs = [];
      this.applicantReviewedStates = [];
      return;
    }

    const primaryApplicant = this.applicantProfileData.primaryApplicant;
    const dependentApplicants =
      this.applicantProfileData.dependentApplicants ?? [];

    this.applicantTabs = [
      {
        applicantId: primaryApplicant.id,
        iconName: 'self',
        label: this.getApplicantFullName(primaryApplicant),
        description: 'Primary Insurer',
      },

      ...dependentApplicants.map((dep, index) => ({
        applicantId: dep.id,
        iconName: 'self-family',
        label: this.getApplicantFullName(dep),
        description: `Family Member ${index + 1}`,
      })),
    ];

    if (this.applicantReviewedStates.length !== this.applicantTabs.length) {
      this.applicantReviewedStates = this.applicantTabs.map(() => false);
      this.inquiryApplicantOptions = this.applicantTabs
        .map((tab) => ({
          value: tab.applicantId ?? '',
          label: tab.label,
        }))
        .filter((option) => !!option.value && !!option.label);
    }
    if (this.applicantSelectedIndex >= this.applicantTabs.length) {
      this.applicantSelectedIndex = 0;
    }
    this.prefillInquiryApplicant();
  }

  // Derives per-applicant reviewed states from API response (applicantReviews array)
  private deriveApplicantReviewedStates(): void {
    const applicantReviews: any[] = this.appReviewData?.applicantReviews ?? [];

    this.applicantReviewedStates = this.applicantTabs.map((tab) => {
      const review = applicantReviews.find(
        (r: any) => r.applicantId === tab.applicantId
      );
      return review?.isApplicantReviewed === true;
    });

    this.updateApplicantTabIcons();
  }

  // Updates applicant tab icons based on reviewed state (green tick or clock)
  private updateApplicantTabIcons(): void {
    this.applicantTabs = this.applicantTabs.map((tab, index) => ({
      ...tab,
      iconNameSuffix: this.applicantReviewedStates[index] ? 'green-tick' : 'icon-clock',
    }));
  }

  syncVerifyDetailsTabIconsForSelectedApplicant(): void {
    const selectedApplicantId = this.applicantTabs[this.applicantSelectedIndex]?.applicantId;
    const selectedApplicantFlags = selectedApplicantId
      ? this.applicantVerifyFlags[selectedApplicantId]
      : undefined;

    this.isApplicantVerified = selectedApplicantFlags?.isApplicantVerified ?? false;
    this.isMafVerified = selectedApplicantFlags?.isMafVerified ?? false;
    this.isDocumentsVerified = selectedApplicantFlags?.isDocumentsVerified ?? false;

    this.verifyDetailsTabs = this.defaultVerifyDetailsTabs.map((tab: VerifyDetailsTab) => ({
      ...tab,
      iconNameSuffix: this.getVerifyTabIconSuffix(tab.label),
    }));
  }

  private getVerifyTabIconSuffix(label: VerifyDetailsTabLabel): string {
    if (label === 'Applicant Profile') {
      return this.isApplicantVerified ? 'green-tick' : 'del-warning';
    }

    if (label === 'MAF') {
      return this.isMafVerified ? 'green-tick' : 'del-warning';
    }

    return this.isDocumentsVerified ? 'green-tick' : 'del-warning';
  }

  private updateVerifyFlagsForAllApplicants(): void {
    const nextFlags: Record<string, ApplicantVerifyFlags> = {};

    this.applicantTabs.forEach((tab) => {
      const applicantId = tab.applicantId;
      if (!applicantId) {
        return;
      }

      nextFlags[applicantId] = {
        isApplicantVerified: this.hasCompleteAdditionalInfoForApplicant(applicantId),
        isMafVerified: this.hasSignedMafForApplicant(applicantId),
        isDocumentsVerified: this.hasDocumentsReviewedForApplicant(applicantId),
      };
    });

    this.applicantVerifyFlags = nextFlags;
  }

  private hasDocumentsReviewedForApplicant(applicantId: string): boolean {
    const applicantReviews: any[] = this.appReviewData?.applicantReviews ?? [];
    const review = applicantReviews.find(
      (item: any) => item?.applicantId === applicantId,
    );

    return review?.isDocumentReviewed === true;
  }

  getSelectedApplicantDocumentReviewed(): boolean {
    const selectedApplicantId = this.applicantTabs[this.applicantSelectedIndex]?.applicantId;
    if (!selectedApplicantId) {
      return false;
    }

    return this.hasDocumentsReviewedForApplicant(selectedApplicantId);
  }

  private hasCompleteAdditionalInfoForApplicant(applicantId: string): boolean {
    const additionalInfo = this.applicantProfileData?.additionalInfo;
    if (!additionalInfo) {
      return false;
    }

    const selectedApplicant = this.getApplicantById(applicantId);
    const isDependentApplicant = this.isDependentApplicant(applicantId);
    const isSponsorSelf = this.isSponsorSelfForAdditionalInfo(additionalInfo.sponsor);

    const requiredValues: Array<string | undefined> = [
      additionalInfo.sponsor,
      additionalInfo.sponsorName,
      additionalInfo.sponsorSalaryBand ?? additionalInfo.sponsorSalary,
      additionalInfo.preferredPolicyStartDate,
      ...(isDependentApplicant ? [selectedApplicant?.relationshipToSelf] : []),
      ...(!isSponsorSelf
        ? [
          additionalInfo.sponsorType ?? additionalInfo.sponsoType,
          additionalInfo.sponsorEmirate,
        ]
        : []),
    ];

    return requiredValues.every((fieldValue) => this.hasMeaningfulValue(fieldValue));
  }

  private getApplicantById(applicantId: string): ApplicantBasicInfoApi | null {
    const primaryApplicant = this.applicantProfileData?.primaryApplicant;
    if (!primaryApplicant) {
      return null;
    }

    if (primaryApplicant.id === applicantId) {
      return primaryApplicant;
    }

    const dependentApplicants = this.applicantProfileData?.dependentApplicants ?? [];
    return dependentApplicants.find((applicant) => applicant.id === applicantId) ?? null;
  }

  private isDependentApplicant(applicantId: string): boolean {
    const primaryApplicantId = this.applicantProfileData?.primaryApplicant?.id;
    return !!primaryApplicantId && applicantId !== primaryApplicantId;
  }

  private isSponsorSelfForAdditionalInfo(sponsor: string | undefined): boolean {
    const sponsorLabel = this.getDropdownLabel(sponsor, WHO_IS_SPONSOR_OPTIONS);
    return sponsorLabel.trim().toLowerCase() === 'self';
  }

  private hasSignedMafForApplicant(applicantId: string): boolean {
    if (this.hasDirectSignedMafForApplicant(applicantId)) {
      return true;
    }

    if (!this.isOfflineMafMode()) {
      return false;
    }

    const primaryApplicantId = this.getPrimaryApplicantIdForMaf();
    if (!primaryApplicantId || applicantId === primaryApplicantId) {
      return false;
    }

    const isSharedDependent = this.getSharedDependentApplicantIds().includes(applicantId);
    if (!isSharedDependent) {
      return false;
    }

    return this.hasDirectSignedMafForApplicant(primaryApplicantId);
  }

  private hasDirectSignedMafForApplicant(applicantId: string): boolean {
    const submissions = this.appReviewData?.mafInfo?.mafUploadedDocuments?.submissions ?? [];

    return submissions.some((submission: any) => {
      const hasSelectedApplicant = (submission?.applicants ?? []).some(
        (applicant: any) => applicant?.applicantId === applicantId,
      );

      if (!hasSelectedApplicant) {
        return false;
      }

      return (submission?.documents ?? []).some(
        (document: any) => document?.documentType === 'UploadSignedMafForm',
      );
    });
  }

  private isOfflineMafMode(): boolean {
    return this.appReviewData?.mafInfo?.isOnlineForm === false;
  }

  private getPrimaryApplicantIdForMaf(): string {
    const submissions = this.appReviewData?.mafInfo?.mafUploadedDocuments?.submissions ?? [];

    for (const submission of submissions) {
      const primaryApplicant = (submission?.applicants ?? []).find(
        (applicant: any) => applicant?.applicantType === 'Primary Insured',
      );

      if (primaryApplicant?.applicantId) {
        return primaryApplicant.applicantId;
      }
    }

    return this.applicantTabs[0]?.applicantId ?? '';
  }

  private getSharedDependentApplicantIds(): string[] {
    const submissions = this.appReviewData?.mafInfo?.mafUploadedDocuments?.submissions ?? [];
    const sharedDependentIds = new Set<string>();

    submissions.forEach((submission: any) => {
      (submission?.applicants ?? []).forEach((applicant: any) => {
        const applicantId = applicant?.applicantId;
        if (!applicantId) {
          return;
        }

        if (applicant?.applicantType !== 'Primary Insured' && applicant?.mafShared) {
          sharedDependentIds.add(applicantId);
        }
      });
    });

    return Array.from(sharedDependentIds);
  }

  private setMafVerificationFlag(applicantId: string, isVerified: boolean): void {
    const currentFlags = this.applicantVerifyFlags[applicantId] ?? this.createDefaultVerifyFlags();
    this.applicantVerifyFlags[applicantId] = {
      ...currentFlags,
      isMafVerified: isVerified,
    };
  }

  private hasMeaningfulValue(value: unknown): boolean {
    if (value === null || value === undefined) {
      return false;
    }

    const normalizedValue = (
      typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean'
        ? String(value)
        : ''
    ).trim().toLowerCase();

    return normalizedValue.length > 0 && normalizedValue !== '-' && normalizedValue !== 'na' && normalizedValue !== 'n/a';
  }

  private createDefaultVerifyFlags(): ApplicantVerifyFlags {
    return {
      isApplicantVerified: false,
      isMafVerified: false,
      isDocumentsVerified: false,
    };
  }
  get selectedApplicantProfileData(): SingleApplicantData | null {
    if (!this.applicantProfileData?.primaryApplicant) {
      return null;
    }

    const dependentApplicants =
      this.applicantProfileData.dependentApplicants ?? [];
    const selectedApplicant =
      this.applicantSelectedIndex === 0
        ? this.applicantProfileData.primaryApplicant
        : dependentApplicants[this.applicantSelectedIndex - 1];

    if (!selectedApplicant) {
      return null;
    }

    return {
      // From backend we are getting each field values as string or number, the child component expects each field to be in ApplicantField type so we are tranforming api response to ui format using mapper functions

      basicInfo: this.mapSingleApplicantBasicInfo(selectedApplicant),
      additionalInfo: this.mapAdditionalInfo(
        this.applicantProfileData.additionalInfo,
        selectedApplicant,
      ),
      preferences: this.applicantProfileData.preferences ?? {},
      isDependentApplicant: this.applicantSelectedIndex > 0,
      isSponsorSelf: this.isSponsorSelf,
    };
  }

  get isCurrentSponsorSelf(): boolean {
    const sponsorVal = this.applicantProfileData?.additionalInfo?.sponsor;
    if (sponsorVal !== undefined && sponsorVal !== null) {
      const sponsorLabel = this.getDropdownLabel(sponsorVal, WHO_IS_SPONSOR_OPTIONS);
      return sponsorLabel.trim().toLowerCase() === 'self';
    }
    return this.isSponsorSelf;
  }

  private getApplicantFullName(applicant: ApplicantBasicInfoApi): string {
    const firstName = applicant.firstName?.trim() ?? '';
    const lastName = applicant.lastName?.trim() ?? '';
    return `${firstName} ${lastName}`.trim() || 'Applicant';
  }

  private readonly applicantFieldMetadata: Record<
    (typeof ApplicationReview.BASIC_INFO_FIELD_KEYS)[number],
    { label: string; icon: string; verified?: boolean }
  > = {
      firstName: { label: 'First Name', icon: 'icon-user-outline' },
      lastName: { label: 'Last Name', icon: 'icon-user-outline' },
      dob: { label: 'Date of Birth', icon: 'icon-calendar' },
      gender: { label: 'Gender', icon: 'icon-user-outline' },
      emirateOfVisaInsurance: {
        label: 'Emirate of Visa Issuance',
        icon: 'icon-target',
      },
      maritalStatus: { label: 'Marital Status', icon: 'icon-user-group' },
      emirateOfVisaInsuranceId: { label: 'Emirate ID', icon: 'icon-id-card' },
    };

  private mapSingleApplicantBasicInfo(
    applicant: ApplicantBasicInfoApi,
  ): SingleApplicantBasicInfo {
    return {
      firstName: this.toApplicantField(
        applicant.firstName,
        this.applicantFieldMetadata.firstName,
      ),
      lastName: this.toApplicantField(
        applicant.lastName,
        this.applicantFieldMetadata.lastName,
      ),
      dob: this.toApplicantField(
        this.formatDisplayDate(applicant.dob),
        this.applicantFieldMetadata.dob,
      ),
      gender: this.toApplicantField(
        applicant.gender,
        this.applicantFieldMetadata.gender,
      ),
      emirateOfVisaInsurance: this.toApplicantField(
        applicant.emirateOfVisaInsurance,
        this.applicantFieldMetadata.emirateOfVisaInsurance,
      ),
      maritalStatus: this.toApplicantField(
        applicant.maritalStatus,
        this.applicantFieldMetadata.maritalStatus,
      ),
      emirateOfVisaInsuranceId: this.toApplicantField(
        applicant.emirateOfVisaInsuranceId,
        this.applicantFieldMetadata.emirateOfVisaInsuranceId,
      ),
      // id: this.toApplicantField(applicant.id, this.applicantFieldMetadata.id),
      id: applicant.id,
      passportFileName: applicant.passportFileName,
      passportFilePath: applicant.passportFilePath,
      passportFileSize: applicant.passportFileSize,
      passportFileType: applicant.passportFileType,
      passportInfoId: applicant.passportInfoId,
    };
  }

  private toApplicantField(
    value: string | null | undefined,
    metadata: { label: string; icon: string; verified?: boolean },
  ): ApplicantField {
    return {
      label: metadata.label,
      value: value?.trim() ?? '',
      icon: metadata.icon,
      ...(metadata.verified !== undefined
        ? { verified: metadata.verified }
        : {}),
    };
  }

  private mapAdditionalInfo(
    additionalInfo: ApplicantAdditionalInfoApi | undefined,
    selectedApplicant: ApplicantBasicInfoApi,
  ): ApplicantAdditionalInfo {
    const sponsor = this.getDropdownLabel(
      additionalInfo?.sponsor,
      WHO_IS_SPONSOR_OPTIONS,
    );

    this.isSponsorSelf = sponsor.trim().toLowerCase() === 'self';
    const sponsorType = this.getDropdownLabel(
      additionalInfo?.sponsoType ?? additionalInfo?.sponsorType,
      SPONSOR_TYPE_OPTIONS,
    );

    const sponsorEmirate = this.getDropdownLabel(
      additionalInfo?.sponsorEmirate,
      SPONSOR_EMIRATE_OPTIONS,
    );

    const sponsorSalaryBand = this.getDropdownLabel(
      additionalInfo?.sponsorSalaryBand ?? additionalInfo?.sponsorSalary,
      SPONSOR_SALARY_BAND_OPTIONS,
    );

    const relationshipToSelf = this.getDropdownLabel(
      selectedApplicant?.relationshipToSelf,
      RELATIONSHIP_TO_SELF_OPTIONS,
    );

    const sponsorName = additionalInfo?.sponsorName ?? '-';

    return {
      sponsorshipQuestions: [
        {
          label: 'Relationship to Self',
          value: relationshipToSelf,
          icon: 'icon-user-outline',
        },
        {
          label: 'Who is the Sponsor',
          value: sponsor,
          icon: 'icon-user-outline',
        },
        {
          label: 'Sponsor Name / Employer Name',
          value: sponsorName,
          icon: 'icon-user-outline',
        },
        {
          label: 'Sponsor Type',
          value: sponsorType,
          icon: 'sponsor_type_icon',
        },
        {
          label: "Sponsor's Emirates",
          value: sponsorEmirate,
          icon: 'globe_icon',
        },
        {
          label: 'Sponsor Salary Band',
          value: sponsorSalaryBand,
          icon: 'quote-personal-budget',
        },
      ],
      policyDetails: [
        {
          label: 'Policy Start Date',
          value: this.formatPolicyStartDate(
            additionalInfo?.preferredPolicyStartDate,
          ),
          icon: 'icon-calendar',
        },
      ],
      isCommonAcrossApplicants: (additionalInfo?.familyMemberCount ?? 0) > 0,
    };
  }

  private normalizeDisplayValue(value: string | undefined): string {
    return (value ?? '')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/_/g, ' ')
      .trim();
  }

  private formatDisplayDate(value: string | null | undefined): string {
    const normalized = (value ?? '').trim();
    if (!normalized) return '';
    return DateTimeConversionHelper.formatDateForPayload(normalized) || normalized;
  }

  private formatPolicyStartDate(value: string | undefined): string {
    return this.formatDisplayDate(value);
  }
  private getDropdownLabel(
    value: string | undefined,
    options: readonly ApplicantDropdownOption[],
  ): string {
    const normalized = (value ?? '').trim();

    if (!normalized) {
      return '';
    }

    const matchedOption = options.find(
      (option) =>
        option.value.trim().toLowerCase() === normalized.toLowerCase(),
    );

    return matchedOption?.label ?? this.normalizeDisplayValue(normalized);
  }

  // Resolve a mixed incoming status value (enum number, enum key, or UI label) to a ReviewTab.
  private resolveReviewStatus(value: unknown): ReviewTab {
    return this.resolveReviewStatusTab(value) ?? 'Unassigned';
  }

  private resolveReviewStatusTab(value: unknown): ReviewTab | null {
    if (typeof value === 'number') {
      const enumKey = ReviewStatus[value];
      const resolvedTab =
        typeof enumKey === 'string' ? this.getValidReviewTab(enumKey) : null;
      return resolvedTab;
    }

    if (typeof value !== 'string') {
      return null;
    }

    const normalizedValue = value.trim();
    if (!normalizedValue) {
      return null;
    }

    const numericValue = Number(normalizedValue);
    if (Number.isInteger(numericValue)) {
      const enumKey = ReviewStatus[numericValue];
      const resolvedTab =
        typeof enumKey === 'string' ? this.getValidReviewTab(enumKey) : null;
      if (resolvedTab) {
        return resolvedTab;
      }
    }

    const directTab = this.getValidReviewTab(normalizedValue);
    if (directTab) {
      return directTab;
    }

    const aliasTab = this.getReviewStatusAliasTab(normalizedValue);
    if (aliasTab) {
      return aliasTab;
    }

    const labelToTabMap: Record<string, ReviewTab> = {
      Unassigned: 'Unassigned',
      Assigned: 'Assigned',
      'Review In-Progress': 'InProgress',
      'Agent Follow-up': 'AgentFollowUp',
      'Requote Requested': 'AgentFollowUp',
      'Review Completed': 'Completed',
      'Payment Follow-up': 'PaymentFollowUp',
      'Awaiting Policy': 'AwaitingPolicy',
      'Policy Issues': 'PolicyIssues',
      Verified: 'Completed',
    };

    const mappedTab =
      labelToTabMap[normalizedValue];
    if (mappedTab) {
      return mappedTab;
    }

    const enumValue = ReviewStatus[normalizedValue as keyof typeof ReviewStatus];
    if (typeof enumValue === 'number') {
      const enumKey = ReviewStatus[enumValue];
      const resolvedTab =
        typeof enumKey === 'string' ? this.getValidReviewTab(enumKey) : null;
      return resolvedTab;
    }

    return null;
  }

  private getValidReviewTab(status: string): ReviewTab | null {
    const allowedTabs: ReviewTab[] = [
      'Unassigned',
      'Assigned',
      'InProgress',
      'AgentFollowUp',
      'Completed',
      'PaymentFollowUp',
      'AwaitingPolicy',
      'PolicyIssues',
    ];

    return allowedTabs.includes(status as ReviewTab)
      ? (status as ReviewTab)
      : null;
  }

  private getReviewStatusAliasTab(status: string): ReviewTab | null {
    const normalizedStatus = status.replace(/[\s_-]/g, '').toLowerCase();
    const aliases: Record<string, ReviewTab> = {
      unassigned: 'Unassigned',
      assigned: 'Assigned',
      inprogress: 'InProgress',
      reviewinprogress: 'InProgress',
      agentfollowup: 'AgentFollowUp',
      requoterequested: 'AgentFollowUp',
      completed: 'Completed',
      reviewcompleted: 'Completed',
      verified: 'Completed',
      paymentfollowup: 'PaymentFollowUp',
      awaitingpayment: 'PaymentFollowUp',
      paymentfailed: 'PaymentFollowUp',
      awaitingpolicy: 'AwaitingPolicy',
      policyissues: 'PolicyIssues',
    };

    return aliases[normalizedStatus] ?? null;
  }

  private mapReviewTabToStatusLabel(reviewTab: ReviewTab): ReviewAssignmentStatus {
    const statusLabelMap: Record<ReviewTab, ReviewAssignmentStatus> = {
      Unassigned: 'Unassigned',
      Assigned: 'Assigned',
      InProgress: 'Review In-Progress',
      AgentFollowUp: 'Agent Follow-up',
      Completed: 'Review Completed',
      PaymentFollowUp: 'Payment Follow-up',
      AwaitingPolicy: 'Awaiting Policy',
      PolicyIssues: 'Policy Issues',
    };

    return statusLabelMap[reviewTab];
  }

  private hasUnsavedDocumentChanges(): boolean {
    if (this.isViewMode) {
      return false;
    }

    return this.uploadDocumentsPreviewRef?.hasUnsavedDocumentChanges() === true;
  }

  private hasUnsavedApplicantProfileChanges(): boolean {
    if (this.isViewMode) {
      return false;
    }

    return this.applicantProfileRef?.hasUnsavedChanges() === true;
  }

  private hasUnsavedReviewChanges(): boolean {
    return (
      this.hasUnsavedApplicantProfileChanges() ||
      this.hasUnsavedDocumentChanges()
    );
  }

  private applyApplicantTabChange(index: number): void {
    this.ngZone.run(() => {
      this.applicantSelectedIndex = index;
      this.updateVerifyFlagsForAllApplicants();
      this.syncVerifyDetailsTabIconsForSelectedApplicant();
      this.cdr.detectChanges();
    });
  }

  private applyVerifyTabChange(index: number): void {
    this.verifySelectedIndex = index;
    const selectedTab = this.verifyDetailsTabs[index];
    this.documentsSelected = selectedTab?.label === 'Documents';
  }

  ngOnDestroy(): void {
    this.unsavedChangesService.destroy();
  }

  private reviewStatusMap: Record<ReviewTab, ReviewStatus> = {
    Unassigned: ReviewStatus.Unassigned,
    Assigned: ReviewStatus.Assigned,
    InProgress: ReviewStatus.InProgress,
    AgentFollowUp: ReviewStatus.AgentFollowUp,
    Completed: ReviewStatus.Completed,
    PaymentFollowUp: ReviewStatus.PaymentFollowUp,
    AwaitingPolicy: ReviewStatus.AwaitingPolicy,
    PolicyIssues: ReviewStatus.PolicyIssues,
  };
}
